import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Typography,
  Box, Stack, TextField, FormControl, InputLabel, Select, MenuItem, Button,
  Paper, Chip, InputAdornment, alpha, useTheme, Divider, CircularProgress,
  Grid,
} from '@mui/material';
import { Close, ArrowBack, AutoAwesome } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import letterCategoryService from '../../services/letterCategoryService';
import letterTemplateService from '../../services/letterTemplateService';
import letterService from '../../services/letterService';
import { LetterCategory, LetterTemplateV2 } from '../../types/letters';
import { severityChipSx, severityTone } from '../../utils/severityTone';
import LetterCategoryPicker from './LetterCategoryPicker';
import { useAuth } from '../../context/AuthContext';

interface Props {
  open: boolean;
  onClose: () => void;
  studentId: string;
  studentName?: string;
  /** Called after a successful issuance — parent should refresh whatever
   * listing is on screen. */
  onIssued?: () => void;
}

interface FormState {
  title: string;
  reason: string;
  description: string;
  amount: string;
  templateId: string;
}

const EMPTY: FormState = { title: '', reason: '', description: '', amount: '', templateId: '' };

/**
 * Two-step issue flow:
 *   1. Pick a category (card grid).
 *   2. Fill in the form — fields render dynamically from the category's
 *      requiresAmount / requiresReason flags. Template is optional; if
 *      picked, the preview interpolates against the form values so the user
 *      sees the letter before submission.
 *
 * Submit calls `POST /student-letters/:studentId` which freezes the content
 * server-side; the FE just forwards the raw inputs.
 */
const IssueLetterDialog: React.FC<Props> = ({ open, onClose, studentId, studentName, onIssued }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { currency } = useAuth();

  const [categories, setCategories] = useState<LetterCategory[]>([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [templates, setTemplates] = useState<LetterTemplateV2[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const [category, setCategory] = useState<LetterCategory | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Server-resolved placeholder dictionary (student / school / session /
  // class / section / father / date). Loaded once per dialog open and merged
  // with live form values for the preview — guarantees the preview shows the
  // same content the backend will freeze at issuance.
  const [serverContext, setServerContext] = useState<Record<string, string>>({});

  // Reset state on open/close.
  useEffect(() => {
    if (!open) {
      setCategory(null);
      setTemplates([]);
      setServerContext({});
    }
  }, [open]);

  // Pull the server-resolved placeholder context once on open. One round-trip
  // gives the FE everything it needs for an accurate preview.
  useEffect(() => {
    if (!open || !studentId) return;
    let cancelled = false;
    (async () => {
      try {
        const ctx = await letterService.getPreviewContext(studentId);
        if (!cancelled) setServerContext(ctx);
      } catch {
        // Non-fatal — the preview will fall back to literal placeholders.
        if (!cancelled) setServerContext({});
      }
    })();
    return () => { cancelled = true; };
  }, [open, studentId]);

  // Load categories when the dialog opens. System-generated categories
  // (admission offer / rejection / confirmation) are filtered out — those
  // are reserved for programmatic callers (state-transition side effects),
  // not for officer-driven issuance.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoadingCats(true);
      try {
        const list = await letterCategoryService.listLetterCategories();
        if (!cancelled) setCategories(list.filter(c => !c.isSystemGenerated));
      } catch (err: any) {
        if (!cancelled) enqueueSnackbar(err.message || 'Failed to load categories', { variant: 'error' });
      } finally {
        if (!cancelled) setLoadingCats(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, enqueueSnackbar]);

  // Load templates whenever the picked category changes.
  useEffect(() => {
    if (!category) return;
    let cancelled = false;
    (async () => {
      setLoadingTemplates(true);
      try {
        const res = await letterTemplateService.getLetterTemplates({ categoryId: category._id, limit: 100 });
        if (!cancelled) setTemplates(res.templates);
      } catch (err: any) {
        if (!cancelled) enqueueSnackbar(err.message || 'Failed to load templates', { variant: 'error' });
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    })();
    return () => { cancelled = true; };
  }, [category, enqueueSnackbar]);

  const validationSchema = useMemo(() => {
    if (!category) return yup.object({});
    return yup.object({
      title: category.requiresReason ? yup.string() : yup.string().required('Title is required'),
      reason: category.requiresReason ? yup.string().required('Reason is required') : yup.string(),
      description: yup.string(),
      amount: category.requiresAmount
        ? yup.number().typeError('Amount must be a number').positive('Amount must be positive').required('Amount is required')
        : yup.number().typeError('Amount must be a number').positive('Amount must be positive').notRequired(),
      templateId: yup.string(),
    });
  }, [category]);

  const formik = useFormik<FormState>({
    initialValues: EMPTY,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!category) return;
      setSubmitting(true);
      try {
        await letterService.issueLetter(studentId, {
          categoryId: category._id,
          templateId: values.templateId || undefined,
          title: category.requiresReason ? undefined : values.title.trim(),
          reason: category.requiresReason ? values.reason.trim() : undefined,
          description: values.description.trim() || undefined,
          amount: category.requiresAmount ? Number(values.amount) : undefined,
        });
        enqueueSnackbar('Letter issued', { variant: 'success' });
        onIssued?.();
        handleClose();
      } catch (err: any) {
        enqueueSnackbar(err.message || 'Failed to issue letter', { variant: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  // Pre-select the category's default template once templates load.
  useEffect(() => {
    if (!category) return;
    const defaultId = typeof category.defaultTemplateId === 'string'
      ? category.defaultTemplateId
      : category.defaultTemplateId?._id;
    if (defaultId && templates.some(t => t._id === defaultId)) {
      formik.setFieldValue('templateId', defaultId);
    } else {
      formik.setFieldValue('templateId', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, templates]);

  const selectedTemplate = useMemo(
    () => templates.find(t => t._id === formik.values.templateId) || null,
    [templates, formik.values.templateId]
  );

  // Live preview merges:
  //   1. serverContext — student / school / session / class / father / date
  //      (resolved once on dialog open from the same builder the backend uses).
  //   2. live form values — title / reason / description / amount.
  // Form values take precedence so typing updates the preview in real time.
  const previewContent = useMemo(() => {
    if (!selectedTemplate) return '';
    const formDict: Record<string, string> = {
      '{TITLE}': formik.values.title || '',
      '{REASON}': formik.values.reason || '',
      '{DESCRIPTION}': formik.values.description || '',
      '{AMOUNT}': formik.values.amount || '',
    };
    const dict = { ...serverContext, ...formDict };
    let out = selectedTemplate.content;
    for (const [k, v] of Object.entries(dict)) out = out.split(k).join(v);
    return out;
  }, [selectedTemplate, formik.values, serverContext]);

  const handleClose = () => {
    if (submitting) return;
    formik.resetForm({ values: EMPTY });
    setCategory(null);
    onClose();
  };

  const tone = category ? severityTone(theme, category.severity) : null;

  // Widen the dialog when a template is picked so the preview gets real estate
  // without crowding the form. Width tracks the layout, so picking/clearing a
  // template feels intentional rather than jarring.
  const dialogMaxWidth = selectedTemplate ? 'lg' : 'sm';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={dialogMaxWidth} fullWidth>
      <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {category && (
            <IconButton size="small" onClick={() => setCategory(null)} disabled={submitting}>
              <ArrowBack fontSize="small" />
            </IconButton>
          )}
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {category ? `Issue ${category.label}` : 'Issue Letter'}
            </Typography>
            {studentName && (
              <Typography variant="caption" color="text.secondary">
                To: {studentName}
              </Typography>
            )}
          </Box>
        </Stack>
        <IconButton size="small" onClick={handleClose} disabled={submitting}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {!category ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pick the type of letter you want to issue.
            </Typography>
            <LetterCategoryPicker
              categories={categories}
              loading={loadingCats}
              onSelect={(c) => { setCategory(c); }}
            />
          </Box>
        ) : (
          <form id="issue-letter-form" onSubmit={formik.handleSubmit}>
            {/* Category strip — always full width across the top */}
            <Paper elevation={0} sx={{
              p: 1.5, borderRadius: 1.5,
              bgcolor: alpha(tone!.main, 0.06),
              border: `1px solid ${alpha(tone!.main, 0.3)}`,
              mb: 2,
            }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Chip label={category.label} size="small" sx={severityChipSx(theme, category.severity)} />
                <Typography variant="caption" color="text.secondary">
                  {category.requiresAmount && category.requiresReason
                    ? 'Requires amount and reason'
                    : category.requiresAmount
                      ? 'Requires amount'
                      : category.requiresReason
                        ? 'Requires reason'
                        : 'Title only'}
                </Typography>
              </Stack>
            </Paper>

            {/* Template picker — pinned at the top so the choice steers
                the rest of the form + opens the preview. */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Template (optional)</InputLabel>
              <Select
                name="templateId"
                value={formik.values.templateId}
                onChange={formik.handleChange}
                label="Template (optional)"
                disabled={loadingTemplates}
              >
                <MenuItem value=""><em>No template — write inline</em></MenuItem>
                {templates.map(t => (
                  <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Two-column layout when a template is selected: form left,
                preview right. Collapses to a single column on mobile so the
                preview drops below instead of getting squeezed. */}
            <Grid container spacing={2.5}>
              <Grid size={{ xs: 12, md: selectedTemplate ? 6 : 12 }}>
                <Stack spacing={2}>
                  {!category.requiresReason && (
                    <TextField
                      fullWidth
                      size="small"
                      name="title"
                      label="Title"
                      placeholder='e.g. "Top performer this term"'
                      value={formik.values.title}
                      onChange={formik.handleChange}
                      error={formik.touched.title && Boolean(formik.errors.title)}
                      helperText={formik.touched.title && (formik.errors.title as string)}
                    />
                  )}
                  {category.requiresReason && (
                    <TextField
                      fullWidth
                      size="small"
                      name="reason"
                      label="Reason"
                      multiline
                      minRows={2}
                      placeholder='e.g. "Repeatedly disrupting class"'
                      value={formik.values.reason}
                      onChange={formik.handleChange}
                      error={formik.touched.reason && Boolean(formik.errors.reason)}
                      helperText={formik.touched.reason && (formik.errors.reason as string)}
                    />
                  )}
                  {category.requiresAmount && (
                    <TextField
                      fullWidth
                      size="small"
                      name="amount"
                      label="Amount"
                      type="number"
                      inputProps={{ min: 0, step: 1 }}
                      InputProps={{ startAdornment: <InputAdornment position="start">{currency}</InputAdornment> }}
                      value={formik.values.amount}
                      onChange={formik.handleChange}
                      error={formik.touched.amount && Boolean(formik.errors.amount)}
                      helperText={formik.touched.amount && (formik.errors.amount as string)}
                    />
                  )}
                  <TextField
                    fullWidth
                    size="small"
                    name="description"
                    label="Additional notes (optional)"
                    multiline
                    minRows={3}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                  />
                </Stack>
              </Grid>

              {selectedTemplate && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper elevation={0} sx={{
                    p: 2,
                    borderRadius: 1.5,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 280,
                  }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 1.5, pb: 1, borderBottom: `1px dashed ${theme.palette.divider}` }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <AutoAwesome sx={{ fontSize: 14, color: 'text.disabled' }} />
                        <Typography variant="caption" color="text.disabled">
                          Live preview
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                        Filled server-side at issuance
                      </Typography>
                    </Stack>
                    <Box sx={{ flex: 1, overflowY: 'auto', pr: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          color: 'text.primary',
                          lineHeight: 1.7,
                          fontFamily: '"Source Serif Pro", Georgia, "Times New Roman", serif',
                        }}
                      >
                        {previewContent}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </form>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={submitting}>Cancel</Button>
        {category && (
          <Button
            type="submit"
            form="issue-letter-form"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ borderRadius: 2 }}
          >
            {submitting ? 'Issuing…' : 'Issue Letter'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default IssueLetterDialog;
